import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Briefcase, Inbox } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function timeAgo(iso) {
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
    const days = Math.floor(diff / 86400);
    if (days < 30) return `${days} day${days > 1 ? "s" : ""} ago`;
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/jobs`)
      .then((r) => setJobs(r.data || []))
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="careers-page">
      <section className="py-20 md:py-28 border-b border-hairline">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8">
            <div className="overline text-terracotta mb-4">Careers</div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-medium tracking-tight leading-[0.98] text-ink">
              Build a career at
              <br />
              the firm <span className="italic font-light text-forest">HR firms envy.</span>
            </h1>
          </div>
          <div className="md:col-span-4">
            <p className="text-muted2 leading-relaxed">
              We hire recruiters, payroll analysts, and HR consultants who want to own a
              function, not chase a quota.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <SectionHeading
              overline="Open roles"
              title={loading ? "Loading positions…" : `${jobs.length} position${jobs.length === 1 ? "" : "s"} currently open`}
            />
            <div className="text-sm text-muted2">
              Don&rsquo;t see your role?{" "}
              <Link to="/apply" data-testid="careers-general-apply" className="text-forest underline underline-offset-4">
                Send us your CV
              </Link>
            </div>
          </div>

          {loading ? (
            <div data-testid="careers-loading" className="py-16 text-center text-muted2">
              Loading…
            </div>
          ) : jobs.length === 0 ? (
            <div data-testid="careers-empty" className="py-20 text-center border border-hairline">
              <Inbox className="w-10 h-10 mx-auto text-muted2 mb-4" strokeWidth={1.3} />
              <div className="font-display text-xl text-ink">No open positions right now</div>
              <p className="text-sm text-muted2 mt-3 max-w-md mx-auto">
                We&rsquo;re always open to conversations with strong talent. Send your CV
                and we&rsquo;ll reach out when a fitting role opens up.
              </p>
              <Link to="/apply" className="btn-primary mt-6 inline-flex">
                Send your CV <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <div className="border-t border-hairline">
              {jobs.map((j) => (
                <div
                  key={j.id}
                  data-testid={`job-${j.id}`}
                  className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 py-8 border-b border-hairline items-start md:items-center group"
                >
                  <div className="md:col-span-5">
                    <div className="font-display text-xl md:text-2xl font-medium text-ink group-hover:text-forest transition-colors">
                      {j.title}
                    </div>
                    {j.team && <div className="mt-2 text-sm text-muted2">{j.team}</div>}
                  </div>
                  <div className="md:col-span-2 flex items-center gap-2 text-sm text-ink">
                    <MapPin className="w-4 h-4 text-forest" strokeWidth={1.5} />
                    {j.location}
                  </div>
                  <div className="md:col-span-2 flex items-center gap-2 text-sm text-ink">
                    <Briefcase className="w-4 h-4 text-forest" strokeWidth={1.5} />
                    {j.type}
                  </div>
                  <div className="md:col-span-1 text-xs uppercase tracking-[0.2em] text-muted2">
                    {timeAgo(j.created_at)}
                  </div>
                  <div className="md:col-span-2 md:text-right">
                    <Link
                      to={`/apply?role=${encodeURIComponent(j.title)}&job=${j.id}`}
                      data-testid={`apply-${j.id}`}
                      className="inline-flex items-center gap-2 text-sm text-forest font-medium border-b border-forest/40 pb-1 hover:border-terracotta hover:text-terracotta transition-colors"
                    >
                      Apply <ArrowUpRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#F1EAEC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <SectionHeading overline="Life at Novacore" title="Operator-led. Consultant-driven." />
          </div>
          <div className="md:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              ["Learning budget", "₹40,000/year for courses, certifications, and industry conferences."],
              ["Hybrid by design", "3 days in office, 2 days remote — with quarterly team offsites."],
              ["Health & wellness", "Comprehensive family cover plus a monthly wellness stipend."],
              ["ESOP for tenure", "Vested ownership for consultants who stay four years and beyond."],
            ].map(([t, d]) => (
              <div key={t} className="border-t border-forest/20 pt-5">
                <div className="font-display text-lg font-medium text-ink">{t}</div>
                <div className="mt-2 text-sm text-muted2 leading-relaxed">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
